import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, SparkModalPortal } from '@spark';
import DatasetSourceDetailsBody from '@/features/datasets/components/DataCollection/DatasetSourceDetails/DatasetSourceDetailsBody';
import {
    DatasetSourceKind,
    formatCreatedByUser,
    normalizeSourceConfig,
} from '@/features/datasets/utils/datasetSourceConfig';
import { isRetrievalAgentInProgressForDataset } from '@/features/datasets/utils/retrievalAgentProgress';
import { formatDateTime } from '@/shared/utils/helpers';
import { DatasetOrigin } from '@/shared/domain/taskTypes';
import { GlobalContext } from '@/app/providers/globalState';
import '@/features/datasets/components/DataCollection/GenerateDataWithAI/generateWithAI.css';

/**
 * Read-only view of the Generate Data with AI / Retrieval Agent details
 * saved on the dataset (Figma 5132:15799 / 5134:16925).
 */
const DatasetSourceDetailsModal = ({
    open,
    onClose,
    origin,
    sourceConfig,
    createdByUser,
    createdDatetime,
    datasetName,
    configLoading = false,
}) => {
    const { progressStatus } = useContext(GlobalContext) || {};
    const config = normalizeSourceConfig(sourceConfig);
    const isRetrieval =
        config?.source === DatasetSourceKind.retrievalAgent
        || origin === DatasetOrigin.specific_ai_agent;
    const retrievalInProgress =
        isRetrieval && isRetrievalAgentInProgressForDataset(progressStatus, datasetName);
    const createdBy = formatCreatedByUser(createdByUser);
    const createdAt = formatDateTime(config?.createdAt || createdDatetime);
    const metaParts = [
        createdBy ? `Created by ${createdBy}` : null,
        createdAt || null,
    ].filter(Boolean);
    // Defer unmount so the same click cannot land on the AI-generated pill
    // under the overlay (wizard Data Collection has two pills in one card).
    const handleClose = () => {
        window.setTimeout(onClose, 0);
    };

    return (
        <SparkModalPortal
            open={open}
            onClose={handleClose}
            destroyOnClose
            size="slot"
            style={{ zIndex: 2000 }}
        >
            <Modal
                open
                className="gdai-source-details-modal"
                title={isRetrieval ? 'Dataset Retrieval Agent' : 'AI-generated Dataset'}
                showSubtitle={false}
                showOverlay={false}
                showTertiaryButton
                tertiaryLabel="Close"
                showMainButton={false}
                showFooterSlot={metaParts.length > 0}
                footerSlot={
                    metaParts.length ? (
                        <div className="gdai-modal__meta fs-unmask">
                            {metaParts.map((part) => (
                                <span key={part}>{part}</span>
                            ))}
                        </div>
                    ) : null
                }
                onCloseClick={handleClose}
                onTertiaryClick={handleClose}
            >
                <DatasetSourceDetailsBody
                    config={config}
                    raw={sourceConfig}
                    retrievalInProgress={retrievalInProgress}
                    configLoading={configLoading}
                />
            </Modal>
        </SparkModalPortal>
    );
};

DatasetSourceDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    origin: PropTypes.string,
    sourceConfig: PropTypes.object,
    createdByUser: PropTypes.object,
    createdDatetime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    datasetName: PropTypes.string,
    configLoading: PropTypes.bool,
};

export default DatasetSourceDetailsModal;
